import React, { Suspense } from 'react';
import { Route, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LoadingSpinner from '../components/common/LoadingSpinner';

// Lazy load pages
const LoginPage = React.lazy(() => import('../pages/LoginPage'));
const ResetPasswordPage = React.lazy(
  () => import('../pages/ResetPasswordPage')
);
const RegisterPage = React.lazy(() => import('../pages/RegisterPage'));
const ForgotPasswordPage = React.lazy(
  () => import('../pages/ForgotPasswordPage')
);
const ForgotPasswordSentPage = React.lazy(
  () => import('../pages/ForgotPasswordSentPage')
);
const GoogleCallbackPage = React.lazy(
  () => import('../pages/GoogleCallbackPage')
);
const GoogleOnboardingPage = React.lazy(
  () => import('../pages/GoogleOnboardingPage')
);
const LinkBridgePage = React.lazy(() => import('../pages/LinkBridgePage'));
const ConfirmEmailPage = React.lazy(() => import('../pages/ConfirmEmailPage'));
const ConfirmEmailPendingPage = React.lazy(
  () => import('../pages/ConfirmEmailPendingPage')
);

interface PublicRoutesProps {
  isAuthenticated: boolean;
}

const PublicRoutes: React.FC<PublicRoutesProps> = ({ isAuthenticated }) => {
  return (
    <>
      <Route
        path="/login"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <LoginPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/reset-password"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ResetPasswordPage />
            </motion.div>
          </Suspense>
        }
      />

      <Route
        path="/register"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <RegisterPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/forgot-password"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ForgotPasswordPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      <Route
        path="/forgot-password/sent"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ForgotPasswordSentPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Google OAuth callback — siempre accesible (Google redirige aquí) */}
      <Route
        path="/login/callback"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <GoogleCallbackPage />
          </Suspense>
        }
      />

      {/* Completar registro luego de autenticarse con Google */}
      <Route
        path="/google-complete"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <GoogleOnboardingPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />

      {/* Bridge para links recibidos por email (/link?action=...&token=...&email=...) */}
      <Route
        path="/link"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <LinkBridgePage />
          </Suspense>
        }
      />

      {/* Confirmación de cuenta desde link de email */}
      <Route
        path="/confirm-account"
        element={
          <Suspense fallback={<LoadingSpinner />}>
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.3 }}
            >
              <ConfirmEmailPage />
            </motion.div>
          </Suspense>
        }
      />

      {/* Estado post-registro: pendiente de confirmación */}
      <Route
        path="/confirm-email-pending"
        element={
          !isAuthenticated ? (
            <Suspense fallback={<LoadingSpinner />}>
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.3 }}
              >
                <ConfirmEmailPendingPage />
              </motion.div>
            </Suspense>
          ) : (
            <Navigate to="/" replace />
          )
        }
      />
    </>
  );
};

export default PublicRoutes;
